import React, { useState, useEffect } from 'react';

const ProductModal = ({ isOpen, onClose, onSave, product = null }) => {
  useEffect(() => {
    if (isOpen) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [isOpen]);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    imageUrl: '',
    stock: 0,
    category: 'cheat',
    active: true,
    hasPlatforms: false,
    displayOrder: 999,
    // Single price
    price: '',
    originalPrice: '',
    // Platform prices
    androidPrice: '',
    androidOriginalPrice: '',
    iosPrice: '',
    iosOriginalPrice: '',
    features: '',
    deliveryInfo: ''
  });

  useEffect(() => {
    if (product) {
      // Edit mode
      setFormData({
        ...product,
        hasPlatforms: !!product.platforms,
        androidPrice: product.platforms?.android?.price || '',
        androidOriginalPrice: product.platforms?.android?.originalPrice || '',
        iosPrice: product.platforms?.ios?.price || '',
        iosOriginalPrice: product.platforms?.ios?.originalPrice || '',
        features: Array.isArray(product.features) ? product.features.join(', ') : '',
        price: product.price || '',
        originalPrice: product.originalPrice || '',
        imageUrl: product.imageUrl || '',
        displayOrder: product.displayOrder || 999
      });
    }
  }, [product]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const productData = {
      id: product?.id || Date.now(),
      productId: product?.productId || `gc-${Date.now()}`,
      name: formData.name,
      description: formData.description,
      imageUrl: formData.imageUrl,
      stock: parseInt(formData.stock),
      category: formData.category,
      active: formData.active,
      features: formData.features.split(',').map(f => f.trim()).filter(f => f),
      deliveryInfo: formData.deliveryInfo
    };

    if (formData.hasPlatforms) {
      productData.platforms = {
        android: {
          price: parseFloat(formData.androidPrice),
          originalPrice: parseFloat(formData.androidOriginalPrice) || parseFloat(formData.androidPrice)
        },
        ios: {
          price: parseFloat(formData.iosPrice),
          originalPrice: parseFloat(formData.iosOriginalPrice) || parseFloat(formData.iosPrice)
        }
      };
    } else {
      productData.price = parseFloat(formData.price);
      productData.originalPrice = parseFloat(formData.originalPrice) || parseFloat(formData.price);
    }

    onSave(productData);
    onClose();
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      imageUrl: '',
      stock: 0,
      category: 'cheat',
      active: true,
      hasPlatforms: false,
      displayOrder: 999,
      price: '',
      originalPrice: '',
      androidPrice: '',
      androidOriginalPrice: '',
      iosPrice: '',
      iosOriginalPrice: '',
      features: '',
      deliveryInfo: ''
    });
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{product ? 'Edit Product' : 'Add New Product'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="product-form">
          <div className="form-row">
            <div className="form-group">
              <label>Product Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
              />
            </div>

            <div className="form-group">
              <label>Category *</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
              >
                <option value="cheat">Cheat</option>
                <option value="gaming">Gaming</option>
                <option value="software">Software</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Description *</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              rows="3"
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Image URL *</label>
              <input
                type="url"
                value={formData.imageUrl}
                onChange={(e) => setFormData({...formData, imageUrl: e.target.value})}
                placeholder="https://..."
                required
              />
            </div>

            <div className="form-group">
              <label>Stock *</label>
              <input
                type="number"
                value={formData.stock}
                onChange={(e) => setFormData({...formData, stock: e.target.value})}
                min="0"
                required
              />
            </div>

            <div className="form-group">
              <label>Display Order *</label>
              <input
                type="number"
                value={formData.displayOrder}
                onChange={(e) => setFormData({...formData, displayOrder: e.target.value})}
                min="1"
                placeholder="1, 2, 3..."
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.hasPlatforms}
                onChange={(e) => setFormData({...formData, hasPlatforms: e.target.checked})}
              />
              Has Platform-Specific Pricing (Android/iOS)
            </label>
          </div>

          {formData.hasPlatforms ? (
            <div className="platform-pricing">
              <h3>Platform Pricing</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Android Price *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.androidPrice}
                    onChange={(e) => setFormData({...formData, androidPrice: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Android Original Price</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.androidOriginalPrice}
                    onChange={(e) => setFormData({...formData, androidOriginalPrice: e.target.value})}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>iOS Price *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.iosPrice}
                    onChange={(e) => setFormData({...formData, iosPrice: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>iOS Original Price</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.iosOriginalPrice}
                    onChange={(e) => setFormData({...formData, iosOriginalPrice: e.target.value})}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="form-row">
              <div className="form-group">
                <label>Price *</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({...formData, price: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Original Price (for discount)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.originalPrice}
                  onChange={(e) => setFormData({...formData, originalPrice: e.target.value})}
                />
              </div>
            </div>
          )}

          <div className="form-group">
            <label>Features (comma separated)</label>
            <input
              type="text"
              value={formData.features}
              onChange={(e) => setFormData({...formData, features: e.target.value})}
              placeholder="Feature 1, Feature 2, Feature 3"
            />
          </div>

          <div className="form-group">
            <label>Delivery Info</label>
            <input
              type="text"
              value={formData.deliveryInfo}
              onChange={(e) => setFormData({...formData, deliveryInfo: e.target.value})}
              placeholder="Delivered within 5 minutes"
            />
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.active}
                onChange={(e) => setFormData({...formData, active: e.target.checked})}
              />
              Active (visible on store)
            </label>
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn-cancel">
              Cancel
            </button>
            <button type="submit" className="btn-save">
              {product ? 'Update Product' : 'Add Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductModal;
